import { useSupabaseTable } from "./useSupabaseTable";
import { useSupabaseRecord } from "./useSupabaseRecord";
import { useCollection } from "./useCollection";
import { adminArticles, type AdminArticle } from "@/mocks/newsAdmin";
import { videos, type VideoItem } from "@/mocks/videos";
import { liveControlSeed, type LiveControlSettings } from "@/mocks/liveAdmin";
import {
  siteSettingsSeed,
  socialSettingsSeed,
  gallerySeeds,
  advertSeeds,
  customSocialLinkSeeds,
  type SiteSettings,
  type SocialSettings,
  type GalleryAlbum,
  type BreakingItem,
  type Advert,
  type CustomSocialLink,
} from "@/mocks/cms";

export interface GalleryImage {
  id: string;
  albumId: string;
  url: string;
  caption: string;
  sortOrder: number;
}

/** News articles (admin + public share the same table; public filters published). */
export function useNews() {
  return useSupabaseTable<AdminArticle>("news", adminArticles);
}

/** Breaking news ticker items (active items shown in the homepage marquee). */
export function useBreakingNews() {
  return useSupabaseTable<BreakingItem>("breaking_news", []);
}

/** Videos (admin uploads, public displays). */
export function useVideos() {
  return useSupabaseTable<VideoItem>("videos", videos);
}

/** Live TV control settings (single record). */
export function useLiveSettings() {
  return useSupabaseRecord<LiveControlSettings>("live_settings", liveControlSeed);
}

/** Website settings — email, phone, address, branding (single record). */
export function useSiteSettings() {
  return useSupabaseRecord<SiteSettings>("site_settings", siteSettingsSeed);
}

/** Social media links (single record). */
export function useSocialLinks() {
  return useSupabaseRecord<SocialSettings>("social_links", socialSettingsSeed);
}

/** Extra social platforms added by the admin (dynamic list). */
export function useCustomSocialLinks() {
  return useSupabaseTable<CustomSocialLink>("custom_social_links", customSocialLinkSeeds);
}

/** Gallery albums + their images. */
export function useGallery() {
  const albums = useSupabaseTable<GalleryAlbum>("gallery_albums", gallerySeeds);
  const images = useSupabaseTable<GalleryImage>("gallery_images", []);
  return { albums, images };
}

/** Advertisements / announcements published on the homepage (shared with admin CRUD). */
export function useAdverts() {
  return useCollection<Advert>("ltv_adverts_v1", advertSeeds);
}